"use client";

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { sendQuoteRequests } from "@/app/actions/messaging";

interface ShortlistEntry {
  userId: string;
  name: string;
  photoUrl: string | null;
}

interface ShortlistContextValue {
  items: ShortlistEntry[];
  toggle: (entry: ShortlistEntry) => void;
  has: (userId: string) => boolean;
  isFull: boolean;
}

const ShortlistContext = createContext<ShortlistContextValue>({
  items: [],
  toggle: () => {},
  has: () => false,
  isFull: false,
});

export function useQuoteShortlist() {
  return useContext(ShortlistContext);
}

export function QuoteShortlistProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ShortlistEntry[]>([]);
  const [showForm, setShowForm] = useState(false);

  const toggle = useCallback((entry: ShortlistEntry) => {
    setItems((prev) => {
      const exists = prev.some((e) => e.userId === entry.userId);
      if (exists) return prev.filter((e) => e.userId !== entry.userId);
      if (prev.length >= 5) return prev;
      return [...prev, entry];
    });
  }, []);

  const has = useCallback(
    (userId: string) => items.some((e) => e.userId === userId),
    [items]
  );

  const isFull = items.length >= 5;

  return (
    <ShortlistContext.Provider value={{ items, toggle, has, isFull }}>
      {children}
      {items.length > 0 && !showForm && (
        <ShortlistBar
          items={items}
          onRemove={(userId) =>
            setItems((prev) => prev.filter((e) => e.userId !== userId))
          }
          onSendRequest={() => setShowForm(true)}
        />
      )}
      {showForm && (
        <QuoteRequestForm
          items={items}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setItems([]);
          }}
        />
      )}
    </ShortlistContext.Provider>
  );
}

function ShortlistBar({
  items,
  onRemove,
  onSendRequest,
}: {
  items: ShortlistEntry[];
  onRemove: (userId: string) => void;
  onSendRequest: () => void;
}) {
  return (
    <div className="fixed bottom-14 sm:bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-forest shadow-[0_-4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.4)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
          <span className="text-xs font-semibold text-text-muted flex-shrink-0">
            Quote from ({items.length}/5):
          </span>
          {items.map((item) => (
            <span
              key={item.userId}
              className="inline-flex items-center gap-1.5 bg-sage-pale text-forest pl-1.5 pr-2 py-1 rounded-full text-xs font-semibold flex-shrink-0"
            >
              <span className="w-5 h-5 rounded-full bg-cream flex items-center justify-center overflow-hidden flex-shrink-0">
                {item.photoUrl ? (
                  <Image
                    src={item.photoUrl}
                    alt=""
                    width={20}
                    height={20}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[9px] font-bold text-forest">
                    {item.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                )}
              </span>
              {item.name.split(" ")[0]}
              <button
                onClick={() => onRemove(item.userId)}
                className="ml-0.5 text-forest/60 hover:text-forest"
                aria-label={`Remove ${item.name}`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
        <button
          onClick={onSendRequest}
          className="flex-shrink-0 px-5 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm"
        >
          Send Quote Request
        </button>
      </div>
    </div>
  );
}

function QuoteRequestForm({
  items,
  onClose,
  onSuccess,
}: {
  items: ShortlistEntry[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [description, setDescription] = useState("");
  const [preferredDates, setPreferredDates] = useState("");
  const [budgetLow, setBudgetLow] = useState("");
  const [budgetHigh, setBudgetHigh] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Focus management: move focus into the dialog on open, restore on close.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const focusable = dialogRef.current?.querySelector<HTMLElement>(
      'input, textarea, button, [href], select, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  // Escape to close + basic focus trap (Tab / Shift+Tab cycle within dialog).
  useEffect(() => {
    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusables = Array.from(
          dialog.querySelectorAll<HTMLElement>(
            'input, textarea, button, [href], select, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !dialog.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !dialog.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleSubmit() {
    if (!description.trim() || description.trim().length < 20) {
      setError("Please describe the job in at least 20 characters.");
      return;
    }

    const low = budgetLow ? parseInt(budgetLow) : null;
    const high = budgetHigh ? parseInt(budgetHigh) : null;

    if (low !== null && low <= 0) {
      setError("Budget must be a positive number.");
      return;
    }
    if (high !== null && low !== null && high <= low) {
      setError("Upper budget must be higher than the lower budget.");
      return;
    }

    setSubmitting(true);
    setError("");

    const result = await sendQuoteRequests(
      items.map((i) => i.userId),
      {
        description: description.trim(),
        preferredDates: preferredDates.trim(),
        budgetLow: low,
        budgetHigh: high,
      }
    );

    if (result.error) {
      setError(result.error);
      setSubmitting(false);
      return;
    }

    onSuccess();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-charcoal/40 dark:bg-black/60" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-modal-title"
        className="relative bg-white w-full sm:max-w-lg sm:rounded-md shadow-xl max-h-[90vh] overflow-y-auto rounded-t-xl"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h2
              id="quote-modal-title"
              className="font-serif text-xl font-bold text-charcoal"
            >
              Request quotes
            </h2>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-body p-1"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-text-muted mb-5">
            Sending to {items.length} tradesperson{items.length !== 1 ? "s" : ""}:{" "}
            {items.map((i) => i.name.split(" ")[0]).join(", ")}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-sm text-xs text-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-text-body mb-2 block">
                Describe the job
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="What work do you need done? Include any relevant details like size, materials, or special requirements."
                className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest resize-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-text-body mb-2 block">
                Preferred dates
              </label>
              <input
                type="text"
                value={preferredDates}
                onChange={(e) => setPreferredDates(e.target.value)}
                placeholder="e.g. Next week, weekends only, before end of June"
                className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
              />
              <p className="text-[11px] text-text-muted mt-1.5">Optional</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-text-body mb-2 block">
                Budget range
              </label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">£</span>
                  <input
                    type="number"
                    min="1"
                    value={budgetLow}
                    onChange={(e) => setBudgetLow(e.target.value)}
                    placeholder="From"
                    className="w-full pl-7 pr-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
                  />
                </div>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">£</span>
                  <input
                    type="number"
                    min="1"
                    value={budgetHigh}
                    onChange={(e) => setBudgetHigh(e.target.value)}
                    placeholder="To"
                    className="w-full pl-7 pr-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
                  />
                </div>
              </div>
              <p className="text-[11px] text-text-muted mt-1.5">Optional</p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm disabled:opacity-50"
            >
              {submitting
                ? "Sending..."
                : `Send to ${items.length} tradesperson${items.length !== 1 ? "s" : ""}`}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 text-sm text-text-muted hover:text-text-body transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
